import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Send, Pencil, Trash2, ArrowRight, Bot, Info } from "lucide-react";

export default function PartnerAIDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () =>
    api
      .get("/my/ai-drafts")
      .then(({ data }) => {
        setDrafts(data.drafts || []);
        setHotels(data.hotels || []);
      })
      .catch((e) => console.debug("fetch failed:", e?.message));
  useEffect(() => {
    load();
  }, []);

  const send = async (bid) => {
    setBusy(bid.bid_id);
    try {
      await api.post(`/my/bids/${bid.bid_id}/send`);
      toast.success("Bid sent to traveller!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(null);
    }
  };

  const discard = async (bid) => {
    if (!window.confirm(`Discard AI draft for ${bid.hotel_name}?`)) return;
    setBusy(bid.bid_id);
    try {
      await api.post(`/my/bids/${bid.bid_id}/discard`);
      toast.success("Draft discarded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/my/bids/${editing.bid_id}`, {
        quoted_price: Number(editing.quoted_price),
        notes: editing.notes,
        valid_until: editing.valid_until,
      });
      toast.success("Draft updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const enabledCount = hotels.filter((h) => h.auto_bid_enabled).length;

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center gap-2 text-blue-700 hw-label">
          <Bot className="w-4 h-4" /> AI Auto-Bidding
        </div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3" data-testid="ai-drafts-heading">
          AI-drafted bids
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          When travellers post booking requests in your city, our AI reads the request and drafts a competitive bid on your behalf — using your historical accepted prices, current market
          conditions, and demand signals. Review, tweak the price, and hit <b>Send</b>.
        </p>

        <div className="mt-6 hw-card p-5 border-l-4 border-l-blue-600 flex items-start gap-3" data-testid="ai-status-card">
          <Info className="w-5 h-5 text-blue-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm">
              <b>{enabledCount}</b> of <b>{hotels.length}</b> hotel{hotels.length === 1 ? "" : "s"} have Auto-Bidding turned on.{" "}
              <Link to="/partner/profile" className="text-blue-700 font-bold hover:underline" data-testid="configure-auto-bid-link">
                Configure in Hotel Profile →
              </Link>
            </div>
            {enabledCount === 0 && (
              <div className="text-xs text-slate-600 mt-1">Turn it on to start receiving AI-drafted bids automatically.</div>
            )}
          </div>
        </div>

        {drafts.length === 0 ? (
          <div className="hw-card p-10 mt-8 text-center text-slate-600" data-testid="ai-drafts-empty">
            <Sparkles className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
            No AI drafts waiting for review. New drafts appear here when travellers post requests in your city.
          </div>
        ) : (
          <div className="grid gap-4 mt-8" data-testid="ai-drafts-list">
            {drafts.map((d) => (
              <div key={d.bid_id} className="hw-card p-6 grid md:grid-cols-12 gap-4 items-start" data-testid={`draft-${d.bid_id}`}>
                <div className="md:col-span-6">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="hw-chip border-emerald-300 bg-emerald-50 text-emerald-800 uppercase font-bold">
                      <Sparkles className="w-3 h-3" /> AI draft
                    </span>
                    {d.parent_bid_id && (
                      <span className="hw-chip border-blue-300 bg-blue-50 text-blue-700 uppercase font-bold">AI counter</span>
                    )}
                    <span className="hw-chip border-slate-300 bg-slate-50 text-slate-700 uppercase">{d.ai_style || "dynamic"}</span>
                    {typeof d.ai_confidence === "number" && (
                      <span className="hw-chip border-amber-300 bg-amber-50 text-amber-700 uppercase">Confidence {d.ai_confidence}%</span>
                    )}
                  </div>
                  <h3 className="hw-head font-bold text-xl text-slate-900" data-testid={`draft-hotel-${d.bid_id}`}>{d.hotel_name}</h3>
                  {d.request && (
                    <div className="text-sm text-slate-500 mt-1">
                      {d.request.city}
                      {d.request.area ? ` · ${d.request.area}` : ""} · {d.request.rooms_needed} rooms · {d.request.event_type} · {d.request.checkin} → {d.request.checkout}
                      {d.request.budget_per_room ? ` · Budget ₹${d.request.budget_per_room}/room` : ""}
                    </div>
                  )}
                  {d.ai_reasoning && (
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-sm text-sm text-slate-700 italic" data-testid={`draft-reasoning-${d.bid_id}`}>
                      <b className="not-italic text-slate-900">AI says: </b>
                      {d.ai_reasoning}
                    </div>
                  )}
                  {d.ai_context && (
                    <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-bold uppercase text-slate-600">
                      {d.ai_context.historical_avg && (
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm">Hist. avg ₹{d.ai_context.historical_avg}</span>
                      )}
                      {d.ai_context.lowest_competing && (
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm">Lowest rival ₹{d.ai_context.lowest_competing}</span>
                      )}
                      {d.ai_context.floor && <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm">Floor ₹{d.ai_context.floor}</span>}
                      {d.ai_context.baseline && <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm">Listed ₹{d.ai_context.baseline}</span>}
                      {d.ai_context.fallback && <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">Heuristic (LLM offline)</span>}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3">
                  <div className="hw-label text-slate-500">AI-suggested quote</div>
                  <div className="hw-head text-4xl font-black text-slate-900" data-testid={`draft-price-${d.bid_id}`}>₹{d.quoted_price.toLocaleString("en-IN")}</div>
                  <div className="text-xs text-slate-500 mt-1">/room · valid till {d.valid_until}</div>
                </div>
                <div className="md:col-span-3 flex flex-col gap-2">
                  <Button
                    onClick={() => send(d)}
                    disabled={busy === d.bid_id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
                    data-testid={`send-draft-${d.bid_id}`}
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    {busy === d.bid_id ? "Sending…" : "Send to traveller"}
                  </Button>
                  <Button
                    onClick={() => setEditing({ ...d })}
                    variant="outline"
                    className="rounded-sm border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"
                    data-testid={`edit-draft-${d.bid_id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1.5" /> Edit price
                  </Button>
                  <Button
                    onClick={() => discard(d)}
                    variant="ghost"
                    className="rounded-sm text-red-700 hover:bg-red-50"
                    data-testid={`discard-draft-${d.bid_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Discard
                  </Button>
                  {d.request && (
                    <Link to={`/requests/${d.request.request_id}`} className="text-xs text-blue-700 font-bold hover:underline text-center mt-1" data-testid={`view-request-${d.bid_id}`}>
                      View request <ArrowRight className="w-3 h-3 inline" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm" data-testid="edit-draft-dialog">
          <DialogHeader>
            <DialogTitle className="hw-head font-black">Edit AI draft — {editing?.hotel_name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="hw-label">Quoted price (₹/room)</Label>
                <Input
                  type="number"
                  value={editing.quoted_price}
                  onChange={(e) => setEditing({ ...editing, quoted_price: Number(e.target.value) })}
                  className="rounded-sm mt-1"
                  data-testid="edit-draft-price"
                />
              </div>
              <div>
                <Label className="hw-label">Notes</Label>
                <Textarea
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  className="rounded-sm mt-1"
                  rows={3}
                  data-testid="edit-draft-notes"
                />
              </div>
              <div>
                <Label className="hw-label">Valid until</Label>
                <Input
                  type="date"
                  value={editing.valid_until}
                  onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })}
                  className="rounded-sm mt-1"
                  data-testid="edit-draft-valid"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="save-edit-draft">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
